import { ActionIcon } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty } from '@semios/app-platform-common'
import type { TFlowMeterMeta } from '@semios/app-platform-value-type-definitions'
import { IconGear } from 'components/icons/IconGear'
import { InfoMessageBox } from 'components/InfoMessageBox/InfoMessageBox'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { colors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { apiFetch } from 'utils/apiFetch'
import { getDateForDayAndMonthPicker } from 'utils/getDateForDayAndMonthPicker'
import { refreshSummaryTab } from 'utils/refreshSummaryTab'
import { showNotification } from 'utils/showNotification'

const GEAR_ICON_SIZE = 14

export const ModalDrawerSeasonSettings = ({
  defaultSeasonStartDate,
  flowMeterData,
  timezone,
}: {
  defaultSeasonStartDate: string
  flowMeterData: routes.Values.Response['points']
  timezone: string
}) => {
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const [seasonSettingsOpened, setSeasonSettingsOpened] = useState(false)
  const [startDate, setStartDate] = useState<string | null>(null)

  useEffect(() => {
    setStartDate(defaultSeasonStartDate)
  }, [defaultSeasonStartDate])

  const isFormTheSameAsDefault = defaultSeasonStartDate === startDate

  const handleCloseWithoutSaving = () => {
    setSeasonSettingsOpened(false)

    setStartDate(defaultSeasonStartDate)
  }

  const handleUpdate = async () => {
    if (isFormTheSameAsDefault || !flowMeterData || isEmpty(flowMeterData)) {
      return
    }

    const seasonStartDate = moment.tz(`${moment.tz().year()}-${startDate}`, timezone).toISOString()

    const args = Object.values(flowMeterData).map((flowMeter, index) => {
      const flowMeterMetadata = flowMeter?.values?.irrigationFlowMeter?.[0]?.metadata as TFlowMeterMeta

      return {
        lngLat: Object.keys(flowMeterData)[index],
        propertyId: Number(selectedPropertyId),
        minFlowRateGpm: flowMeterMetadata?.minFlowRateGpm,
        maxFlowRateGpm: flowMeterMetadata?.maxFlowRateGpm,
        seasonStartDate,
      }
    })

    try {
      const response = await apiFetch({
        url: '/field-asset-settings-set',
        body: {
          irrigationFlowMeterSettingsUpdate: args,
        },
      })

      if (response.irrigationFlowMeterSettingsUpdate) {
        showNotification({
          message: translate.phrases.banyanApp('Successfully updated Season-to-date'),
          type: 'success',
        })

        refreshSummaryTab()

        setSeasonSettingsOpened(false)
      } else {
        showNotification({
          message: translate.phrases.banyanApp('Failed to update Season-to-date'),
          type: 'error',
        })
      }
    } catch (error) {
      showNotification({
        message: translate.phrases.banyanApp('Failed to update Season-to-date'),
        type: 'error',
      })
    }
  }

  return (
    <>
      <ModalDrawer
        title={translate.phrases.banyanApp('Set Season Start Date')}
        opened={seasonSettingsOpened}
        primaryButtonText={translate.phrases.banyanApp('Apply')}
        primaryButtonOnPress={handleUpdate}
        primaryButtonDisabled={isFormTheSameAsDefault}
        secondaryButtonText={translate.phrases.banyanApp('Cancel')}
        secondaryButtonOnPress={handleCloseWithoutSaving}
        onClose={handleCloseWithoutSaving}
        zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX}
      >
        <InfoMessageBox style={{ margin: 20 }}>
          <div css={{ fontSize: 16, fontWeight: 400 }}>
            {translate.phrases.banyanApp('Changes take up to 10 minutes to be reflected on map')}
          </div>
        </InfoMessageBox>
        <div css={{ marginTop: 20, display: 'flex', gap: 10, flexDirection: 'column', padding: '20px' }}>
          <div css={{ fontSize: 16, fontWeight: 500 }}>{translate.phrases.banyanApp('Start Date')}</div>
          <DatePickerInput
            valueFormat={translate.dates.getMomentFormat('MMM D')}
            clearable={false}
            placeholder={translate.phrases.banyanApp('Select start date')}
            monthLabelFormat={translate.dates.getMomentFormat('MMM')}
            value={getDateForDayAndMonthPicker(startDate)}
            onChange={(e) => setStartDate(translate.dates.format(moment.tz(e, moment.tz.guess()), 'MM-DD'))}
            css={{ maxWidth: '100%', width: '240px', marginRight: '15px' }}
          />
        </div>
      </ModalDrawer>
      <ActionIcon
        css={{
          color: colors.grey800,
          fontSize: GEAR_ICON_SIZE,
          padding: 0,
          minWidth: GEAR_ICON_SIZE,
          minHeight: GEAR_ICON_SIZE,
          width: GEAR_ICON_SIZE,
          height: GEAR_ICON_SIZE,
        }}
        variant={'transparent'}
        onClick={() => {
          setSeasonSettingsOpened(true)
        }}
      >
        <IconGear />
      </ActionIcon>
    </>
  )
}
